
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, User, Users, Phone, Info, Building2, ShieldCheck, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, isProfessional, isAdmin } = useAuth();

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById('mobile-sidebar');
      const burgerButton = document.getElementById('burger-button');

      if (
        isOpen &&
        sidebar &&
        burgerButton &&
        !sidebar.contains(event.target as Node) &&
        !burgerButton.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close sidebar when route changes
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname]);

  const isActive = (path: string) => {
    return location.pathname === path ||
           (path !== '/' && location.pathname.startsWith(path));
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
  const avatarInitial = displayName.charAt(0).toUpperCase();

  return (
    <div className={cn(
      "fixed inset-0 z-[46] bg-black bg-opacity-30 backdrop-blur-sm md:hidden transition-opacity duration-300",
      isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
    )}>
      <div
        id="mobile-sidebar"
        className={cn(
          "h-full w-72 bg-white shadow-xl fixed right-0 transform transition-transform duration-300 ease-in-out flex flex-col",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* User profile section */}
        {user ? (
          <div className="px-5 pt-14 pb-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-[16px]">{avatarInitial}</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-[14px] text-brand-text truncate">{displayName}</p>
                {user.email && (
                  <p className="text-[12px] text-brand-text-muted truncate">{user.email}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="pt-14" />
        )}

        <div className="flex flex-col flex-1 overflow-y-auto p-4 gap-1">
          {/* Navigation section */}
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 px-3 mb-1 mt-2">
            Navigation
          </p>
          <SidebarLink
            to="/"
            icon={<Home size={18} />}
            label="Accueil"
            isActive={isActive('/')}
          />
          <SidebarLink
            to="/search"
            icon={<Search size={18} />}
            label="Activités"
            isActive={isActive('/search')}
          />
          <SidebarLink
            to="/blog"
            icon={<BookOpen size={18} />}
            label="Blog"
            isActive={isActive('/blog')}
          />

          {/* Account section */}
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 px-3 mb-1 mt-4">
            Mon compte
          </p>
          <SidebarLink
            to={user ? "/account" : "/auth"}
            icon={<User size={18} />}
            label="Mon Compte"
            isActive={isActive('/account') || isActive('/auth')}
          />
          <SidebarLink
            to="/account?tab=votes"
            icon={<Users size={18} />}
            label="Votes de groupe"
            isActive={isActive('/group-vote') || (location.pathname === '/account' && location.search.includes('tab=votes'))}
          />

          {/* More section */}
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 px-3 mb-1 mt-4">
            Plus
          </p>
          <SidebarLink
            to="/contact"
            icon={<Phone size={18} />}
            label="Contact"
            isActive={isActive('/contact')}
          />
          <SidebarLink
            to="/about"
            icon={<Info size={18} />}
            label="À propos"
            isActive={isActive('/about')}
          />
          <SidebarLink
            to="/pro"
            icon={<Building2 size={18} />}
            label="Devenir Pro"
            isActive={isActive('/pro')}
          />
        </div>

        {/* Role-specific links */}
        {(isProfessional || isAdmin) && (
          <div className="px-4 pb-6 pt-3 border-t border-gray-100 flex flex-col gap-1">
            {isProfessional && (
              <SidebarLink
                to="/pro-dashboard"
                label="Espace Pro"
                icon={<Building2 size={18} />}
                isActive={isActive('/pro-dashboard')}
                className="bg-brand-primary text-white hover:bg-brand-primary/90 border-l-0"
                iconClassName="text-white"
              />
            )}
            {isAdmin && (
              <SidebarLink
                to="/admin"
                icon={<ShieldCheck size={18} />}
                label="Administration"
                isActive={isActive('/admin')}
                className="bg-red-50 text-red-700 hover:bg-red-100 border-l-0"
                iconClassName="text-red-600"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface SidebarLinkProps {
  to: string;
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
  className?: string;
  iconClassName?: string;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({
  to,
  label,
  icon,
  isActive,
  className,
  iconClassName,
}) => {
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 px-3 py-3.5 rounded-xl transition-all duration-200 border-l-2",
        isActive
          ? "border-brand-primary bg-brand-primary/8 text-brand-primary font-semibold"
          : "border-transparent text-gray-700 hover:bg-gray-100 hover:border-gray-200",
        className
      )}
    >
      {icon && (
        <span className={cn(
          "transition-colors flex-shrink-0",
          isActive ? "text-brand-primary" : "text-gray-500",
          iconClassName
        )}>
          {icon}
        </span>
      )}
      <span className="text-[15px]">{label}</span>
    </Link>
  );
};

export default MobileSidebar;
